import { LocationUtils } from '@ctct/rise-utils';
import { Button, cn, Text, useFieldContext } from '@ctct/summit-core';
import { Combobox, type ComboboxItem, type ComboboxTriggerProps } from '@ctct/summit-extended';
import { ChevronDownIcon, ChevronUpIcon } from '@ctct/summit-icons';
import { useTranslations } from 'next-intl';
import { type FocusEvent, forwardRef, useMemo, useRef, useState } from 'react';
import {
  type Country,
  type DefaultInputComponentProps,
  getCountryCallingCode,
} from 'react-phone-number-input';

import { Flag } from './Flag';

const CountrySelectTrigger = forwardRef<
  HTMLButtonElement,
  ComboboxTriggerProps & { onBlur?: (e: FocusEvent) => void }
>(
  (
    { isOpen, onBlur: handleBlur, selected, selectedLabels, disabled: isDisabled, ...props },
    ref
  ) => {
    const { state, inputId } = useFieldContext();

    const ChevronIcon = isOpen ? ChevronUpIcon : ChevronDownIcon;
    const selectedCountry = selected[0] as Country;

    return (
      <Button
        emphasis="medium"
        role="combobox"
        aria-expanded={isOpen}
        aria-label={selectedCountry}
        ref={ref}
        onBlur={handleBlur}
        color="neutral"
        disabled={isDisabled}
        className={cn(
          'r:flex gap-1 r:rounded-r-none r:border-r-0 r:px-3 r:focus:z-raised r:border-neutral-500',
          state === 'error' && 'r:border-danger-500',
          isDisabled && 'r:opacity-100!'
        )}
        id={inputId}
        {...props}
      >
        <Flag
          country={selectedCountry}
          countryName={selectedCountry}
          className={cn(isDisabled && 'r:opacity-50')}
        />
        <ChevronIcon className={cn('r:-mr-2 r:size-4', isDisabled && 'r:invisible')} />
      </Button>
    );
  }
);

const CountrySelect = ({
  disabled: isDisabled,
  options,
  onBlur: handleBlur,
  value: selectedCountry,
  onChange: handleChange,
}: DefaultInputComponentProps) => {
  const t = useTranslations('UiForm');
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const items = useMemo(() => {
    const supportedCountrySet = new Set(
      LocationUtils.countries.map((country) => country.abbreviation)
    );
    return options
      .map((option: { label?: string; value?: Country }) => {
        if (!option.label || !option.value) return null;
        return {
          label: option.label,
          value: option.value as string,
        };
      })
      .filter(
        (item: ComboboxItem | null) =>
          item !== null && supportedCountrySet.has(item.value.toLowerCase())
      );
  }, [options]);

  const renderItems = (item: ComboboxItem) => {
    const { value, label: countryName } = item;
    const countryCode = value as Country;
    return (
      <>
        <Flag country={countryCode} countryName={countryName} />
        <Text className="r:flex-1 r:text-sm">{countryName}</Text>
        <Text className="r:text-sm">{`+${getCountryCallingCode(countryCode)}`}</Text>
      </>
    );
  };

  // Since this combobox is part of the larger phone input component we need to stop propagation if the user
  // uses the keyboard to navigate the combobox.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  // Since this combobox is part of the larger phone input component we need to stop propagation if the user
  // clicks off the combobox to close it.
  const handleComboboxBlur = (e: FocusEvent) => {
    if (!e.relatedTarget) e.stopPropagation();
    handleBlur(e);
  };

  const handleCountryChange = (value: string) => {
    handleChange(value as Country);
    setIsOpen(false);
  };

  return (
    <Combobox
      placeholderText={t('shared.country.comboboxSearchPlaceholder')}
      contentClasses="r:w-80"
      renderTrigger={(props: ComboboxTriggerProps) => (
        <CountrySelectTrigger
          {...props}
          onBlur={handleBlur}
          disabled={isDisabled}
          ref={triggerRef}
        />
      )}
      renderItem={renderItems}
      items={items}
      selected={selectedCountry}
      onBlur={handleComboboxBlur}
      onKeyDown={handleKeyDown}
      onChange={handleCountryChange}
      onOpenChange={setIsOpen}
      isOpen={isOpen}
    />
  );
};

export { CountrySelect };
